import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Box, Button, Grid, TextField, Typography } from "@material-ui/core";
import { Update, Send } from "@material-ui/icons";

import { requestExtension } from "../../../../reducer/internalResearch/action";
import { uploadMultiFile } from "../../../../reducer/file/action";
import MultiFileManager from "../../../File/MultiFileManager/MultiFileManager";
import select from "../../../../utils/select";
import SectionCard from "../../utils/SectionCard";
import {
  INT_DURATION_ONE_YEAR,
  INT_EXTENSION_MONTHS,
} from "../../../../constants/internalResearchStatus";

// RequestExtensionForm — CN xin gia hạn (chỉ đề tài 1y, 1 lần, +6 tháng).
// FE check duration + extendedUntilDate trước khi cho submit (BE cũng enforce).
export default function RequestExtensionForm({ detail, onDone }) {
  const dispatch = useDispatch();
  const profileDetail = useSelector((s) => select(s, "profileReducer", "detail"));

  const [reason, setReason] = useState("");
  const [fileUrls, setFileUrls] = useState([]);
  const [newFiles, setNewFiles] = useState([]);
  const [saving, setSaving] = useState(false);

  const isOneYear = detail?.durationMonths === INT_DURATION_ONE_YEAR;
  const alreadyExtended = !!detail?.extendedUntilDate;
  const canSubmit = isOneYear && !alreadyExtended;

  // Lý do cần chỉnh sửa — có thể tới từ 1 trong 2 nguồn tuỳ đường dẫn dẫn tới
  // status extension_adjust_request:
  //   - admin từ chối thẳng đơn xin gia hạn lần đầu (ReviewExtension adjust_request)
  //     → extensionRequestComment.
  //   - HĐ#2 (1y đã gia hạn) yêu cầu chỉnh sửa → midtermCouncilComment (xem
  //     ReviewMidtermCouncil ở BE, nhánh 1y-đã-gia-hạn).
  const adjustComment = detail?.extensionRequestComment || detail?.midtermCouncilComment || "";

  const commitFiles = async () => {
    if (!newFiles || newFiles.length === 0) return fileUrls.join("||");
    const ownerId = profileDetail?.id || 0;
    const result = await dispatch(uploadMultiFile(ownerId, newFiles, "internalResearch"));
    if (result?.success && Array.isArray(result.data)) {
      const merged = [...fileUrls, ...result.data];
      setFileUrls(merged);
      setNewFiles([]);
      return merged.join("||");
    }
    return fileUrls.join("||");
  };

  const handleSubmit = async () => {
    if (!reason.trim()) {
      alert("Vui lòng nhập lý do xin gia hạn.");
      return;
    }
    setSaving(true);
    try {
      const fileLink = await commitFiles();
      const ok = await dispatch(requestExtension(detail.id, reason, fileLink));
      if (ok && onDone) onDone();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box padding="16px 24px">
      <SectionCard
        icon={<Update />}
        title={`Xin gia hạn đề tài (+${INT_EXTENSION_MONTHS} tháng)`}
        marginTop={0}
      >
        {adjustComment && (
          <Box marginBottom={2} padding="8px 12px" bgcolor="#fafafa" borderRadius={4} border="solid 1px #e0e0e0">
            <Typography variant="caption" color="textSecondary"><b>Lý do cần chỉnh sửa:</b></Typography>
            <Typography variant="body2" style={{ whiteSpace: "pre-wrap" }}>{adjustComment}</Typography>
          </Box>
        )}
        {!isOneYear && (
          <Box padding="10px 14px" bgcolor="#fff3e0" border="solid 1px #fb8c00" borderRadius={4} marginBottom={2}>
            <Typography variant="body2">
              Gia hạn chỉ áp dụng cho đề tài <b>1 năm (12 tháng)</b>. Đề tài này có
              thời lượng {detail?.durationMonths || "?"} tháng.
            </Typography>
          </Box>
        )}
        {alreadyExtended && (
          <Box padding="10px 14px" bgcolor="#fff3e0" border="solid 1px #fb8c00" borderRadius={4} marginBottom={2}>
            <Typography variant="body2">
              Đề tài đã được gia hạn 1 lần đến <b>
                {new Date(detail.extendedUntilDate).toLocaleDateString("vi-VN")}
              </b>. Mỗi đề tài chỉ được gia hạn 1 lần.
            </Typography>
          </Box>
        )}
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="body2" style={{ marginBottom: 6 }}>
              <b>Lý do xin gia hạn</b> <span style={{ color: "#c62828" }}>*</span>
            </Typography>
            <TextField
              fullWidth multiline minRows={4} variant="outlined"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={!canSubmit || saving}
            />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="body2" style={{ marginBottom: 6 }}>
              <b>File đơn (PDF/DOCX)</b>
            </Typography>
            <MultiFileManager
              fileUrls={fileUrls}
              setFileUrls={setFileUrls}
              newFiles={newFiles}
              setNewFiles={setNewFiles}
              disabled={!canSubmit || saving}
            />
          </Grid>
        </Grid>
      </SectionCard>

      {canSubmit && (
        <Box marginTop={3} paddingTop={2} display="flex" justifyContent="flex-end" borderTop="solid 1px #e0e0e0">
          <Button
            variant="contained" color="secondary"
            startIcon={<Send />} disabled={saving}
            onClick={handleSubmit}
          >
            Gửi đơn xin gia hạn
          </Button>
        </Box>
      )}
    </Box>
  );
}
